import { useAuth } from "@/hooks/useAuth";
import "./CsvExportButton.css";

/**
 * Phase 18 WU-18.E1 — CSV download button.
 *
 * Gated on `analytics.export` permission (Administrator-only per WU-18.C
 * security fix). The server is the source of truth; the button is hidden
 * without the permission as UX only.
 *
 * Uses raw `fetch` (not the shared api client) because the response is a
 * text/csv blob, not JSON — the api client's 401 redirect wouldn't help
 * here since CSV paths are protected identically.
 */
export interface CsvExportButtonProps {
  endpoint: string;
  params: Record<string, string>;
  filename: string;
}

export function CsvExportButton({ endpoint, params, filename }: CsvExportButtonProps) {
  const { user } = useAuth();
  if (!user?.permissions?.includes("analytics.export")) return null;

  async function handleClick() {
    const qs = new URLSearchParams(params).toString();
    const url = qs ? `${endpoint}?${qs}` : endpoint;
    const res = await fetch(url, {
      headers: { accept: "text/csv" },
      credentials: "include",
    });
    if (!res.ok) return;
    const blob = await res.blob();
    const href = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = href;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(href);
  }

  return (
    <button
      type="button"
      className="csv-export-btn"
      onClick={handleClick}
      aria-label={`Download CSV (${filename})`}
    >
      Download CSV
    </button>
  );
}
